import React, { useMemo } from "react";
import { Ghost, PacmanState } from "../types/GameTypes";
import { GAME_CONFIG } from "../types/GameConstants";

interface MiniMapProps {
    maze: number[][];
    pacman: PacmanState;
    ghosts: Ghost[];
    visible: boolean;
}

const _SCALE = 2.6;
const _PADDING = 8;

export const MiniMap: React.FC<MiniMapProps> = ({
    maze,
    pacman,
    ghosts,
    visible,
}) => {
    const cols = maze[0]?.length || 0;
    const rows = maze.length;

    const dimensions = useMemo(() => {
        return {
            width: Math.max(96, Math.round(cols * _SCALE) + _PADDING * 2),
            height: Math.max(86, Math.round(rows * _SCALE) + _PADDING * 2),
        };
    }, [cols, rows]);

    if (!visible || rows === 0 || cols === 0) return null;

    const pacmanCellX = Math.floor(pacman.x / GAME_CONFIG.CELL_SIZE);
    const pacmanCellY = Math.floor(pacman.y / GAME_CONFIG.CELL_SIZE);

    return (
        <div
            className="pointer-events-none absolute right-3 top-12 z-20 rounded-lg border border-white/20 bg-black/45 p-2 backdrop-blur-sm"
            style={{ width: dimensions.width, height: dimensions.height }}
            aria-hidden="true"
        >
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-white/70">
                Radar
            </div>
            <svg
                width={dimensions.width - _PADDING * 2}
                height={dimensions.height - _PADDING * 2 - 14}
                viewBox={`0 0 ${cols} ${rows}`}
                className="overflow-visible"
            >
                {maze.map((row, rowIndex) =>
                    row.map((cell, colIndex) => {
                        if (cell !== 1) return null;
                        return (
                            <rect
                                key={`w-${colIndex}-${rowIndex}`}
                                x={colIndex}
                                y={rowIndex}
                                width={1}
                                height={1}
                                fill="#2E3A8A"
                                opacity={0.75}
                            />
                        );
                    }),
                )}

                <circle
                    cx={pacmanCellX + 0.5}
                    cy={pacmanCellY + 0.5}
                    r={0.45}
                    fill="#FDE047"
                />

                {ghosts.map((ghost, index) => (
                    <circle
                        key={`g-${index}-${ghost.color}`}
                        cx={Math.floor(ghost.x / GAME_CONFIG.CELL_SIZE) + 0.5}
                        cy={Math.floor(ghost.y / GAME_CONFIG.CELL_SIZE) + 0.5}
                        r={ghost.mode === "eaten" ? 0.24 : 0.35}
                        fill={ghost.mode === "frightened" ? "#60A5FA" : ghost.color}
                        opacity={ghost.mode === "eaten" ? 0.85 : 1}
                    />
                ))}
            </svg>
        </div>
    );
};
